import {
  getDoc,
  addDoc,
  getDocs,
  deleteDoc,
  setDoc,
  query,
  collection,
  where,
  or,
  and,
  limit,
  doc,
  updateDoc,
  documentId,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { db, auth, storage, ref, uploadBytes, getDownloadURL, deleteObject } from '.';

const emptyStats = {
  points: {
    attempted: [0, 0, 0, 0],
    made: [0, 0, 0, 0]
  },
  rebounds: {
    offensive: 0,
    defensive: 0
  },
  assists: 0,
  fouls: 0
};

export const initializeGameApi = async (selectedTeams, mode = "game") => {
  return new Promise(async (resolve, reject) => {
    try {
      if (mode === "drill") {
        // Drill-specific initialization code if needed, skipping team queries
        resolve({ game: { type_of_game: "drill" }, startNew: true });
        return;
      }
      const gameQuery = query(
        collection(db, 'game'),
        and(
          or(
            where('teamA.name', '==', selectedTeams[0].name),
            where('teamA.name', '==', selectedTeams[1].name)
          ),
          or(
            where('teamB.name', '==', selectedTeams[0].name),
            where('teamB.name', '==', selectedTeams[1].name)
          ),
          where('finished', '!=', true)
        ),
        limit(1)
      );

      const gameQuerySnapshot = await getDocs(gameQuery);
      if (!gameQuerySnapshot.empty) {
        gameQuerySnapshot.forEach(game => {
          const newGameData = game.data();
          const newGame = { id: game.id, ...newGameData, date: newGameData.date.toDate().toISOString() };
          resolve({ game: newGame, startNew: false });
        });
      } else {
        const game = {
          date: serverTimestamp(),
          finished: false,
          actions: []
        };
        const teamsQuery = query(collection(db, 'teams'), where(documentId(), 'in', selectedTeams.map(t => t.id)));
        const teamsQuerySnapshot = await getDocs(teamsQuery);
        teamsQuerySnapshot.forEach(async doc => {
          const playersQuery = query(collection(db, 'players'), where('team', '==', doc.id));
          const playersQuerySnapshot = await getDocs(playersQuery);
          const teamId = doc.id;
          const team = { id: teamId, ...doc.data(), score: 0 };
          const players = {};
          playersQuerySnapshot.forEach((playerDoc) => {
            const playerId = playerDoc.id;
            const playerRawData = playerDoc.data();
            const playerData = {
              id: playerId,
              name: playerRawData.name,
              number: playerRawData.number ? playerRawData.number : '',
              teamId,
              stats: emptyStats
            };
            players[playerId] = playerData;
          });

          if (!game.teamA) {
            game.teamA = { ...team, players };
          } else {
            game.teamB = { ...team, players };
          }

          if (game.teamA && game.teamB) {
            resolve({ game, startNew: true });
          }
        });
      }
    } catch (err) {
      console.log('INITIALIZE DATA API ERR: ', err);
      reject(err);
    }
  });
};
export const addGameApi = async (game) => {
  try {
    const { id: _localId, notSaved: _notSaved, ...gameData } = game;
    const gameToAdd = gameData.date ? gameData : { ...gameData, date: serverTimestamp() };
    const gameRef = collection(db, 'game');
    const newGameRef = await addDoc(gameRef, gameToAdd);
    const newGameSnapshot = await getDoc(newGameRef);
    const newGameData = newGameSnapshot.data();

    let dateString;
    if (newGameData.date) {
      if (typeof newGameData.date.toDate === 'function') {
        dateString = newGameData.date.toDate().toISOString();
      } else if (newGameData.date instanceof Date) {
        dateString = newGameData.date.toISOString();
      } else {
        dateString = newGameData.date;
      }
    }

    return { ...newGameData, id: newGameRef.id, date: dateString };
  } catch (err) {
    console.error('ADD GAME API ERR: ', err);
    throw new Error('Failed to save game to database');
  }
};

const getNewGameStats = ({ game, selectedTeam, player, playerId, newStats, newScore }) => {
  const newGame = {
    [selectedTeam]: {
      ...game[selectedTeam],
      players: {
        ...game[selectedTeam].players,
        [playerId]: {
          ...player,
          stats: newStats
        }
      }
    }
  };

  if (newScore) {
    newGame[selectedTeam].score = newScore;
  }

  return newGame;
};

export const addMadeShotApi = async ({ game, selectedTeam, playerId, points, type_of_game = "game" }) => {
  return new Promise(async (resolve, reject) => {
    try {
      let newStats;
      let newGame;

      if (type_of_game === "drill") {
        // Check if player is part of the drill
        if (!game.playerIds?.includes(playerId)) {
          throw new Error(`Player with ID ${playerId} not found in the drill`);
        }

        // Define new stats for drill mode
        newStats = {
          attempts: (game.stats[playerId]?.attempts || 0) + 1,
          completions: (game.stats[playerId]?.completions || 0) + 1,
        };

        // Update game directly without player/team structure
        newGame = {
          ...game,
          stats: { 
            ...game.stats, 
            [playerId]: newStats 
          },
        };
        console.log('addMadeShotApi',newGame)
      } else {
        // Logic for game mode
        const player = game[selectedTeam]?.players?.[playerId];
        if (!player) {
          throw new Error(`Player with ID ${playerId} not found in ${selectedTeam}`);
        }

        newStats = {
          ...player.stats,
          points: {
            ...player.stats.points,
            made: player.stats.points.made.map((val, index) => (index === points ? val + 1 : val)),
          },
        };

        const newScore = game[selectedTeam].score + parseInt(points);
        newGame = getNewGameStats({ game, selectedTeam, player, playerId, newStats, newScore });
      }

      // Update Firestore with the new game state
      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      resolve();
    } catch (err) {
      console.log('ADD MADE SHOT API ERR: ', err);
      reject(err);
    }
  });
};


export const addAttemptedShotApi = async ({ game, selectedTeam, playerId, points, type_of_game = "game" }) => {
  return new Promise(async (resolve, reject) => {
    try {
      let newStats;
      let newGame;

      if (type_of_game === "drill") {
        // Check if player is part of the drill
        if (!game.playerIds?.includes(playerId)) {
          throw new Error(`Player with ID ${playerId} not found in the drill`);
        }

        newStats = {
          attempts: (game.stats[playerId]?.attempts || 0) + 1,
        };

        newGame = {
          ...game,
          stats: { 
            ...game.stats, 
            [playerId]: newStats 
          }
        };

      } else {
        // For game mode, find the player in the selected team and update points attempted
        const player = game[selectedTeam]?.players?.[playerId];
        if (!player) {
          throw new Error(`Player with ID ${playerId} not found in team ${selectedTeam}`);
        }

        newStats = {
          ...player.stats,
          points: {
            ...player.stats.points,
            attempted: player.stats.points.attempted.map((val, index) => (index === points ? val + 1 : val)),
          }
        };

        newGame = {
          ...game,
          [selectedTeam]: {
            ...game[selectedTeam],
            players: {
              ...game[selectedTeam].players,
              [playerId]: {
                ...player,
                stats: newStats
              }
            }
          }
        };
      }

      // Update Firestore with the new game state
      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      console.log('addAttemptedShotApi:', newGame);
      resolve();
    } catch (err) {
      console.log('ADD ATTEMPTED SHOT API ERR:', err);
      reject(err);
    }
  });
};


export const addReboundApi = async ({ game, selectedTeam, playerId, type }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const player = game[selectedTeam].players[playerId];
      const newStats = {
        ...player.stats,
        rebounds: {
          ...player.stats.rebounds,
          [type]: player.stats.rebounds[type] + 1
        }
      };

      const newGame = getNewGameStats({ game, selectedTeam, player, playerId, newStats });
      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      resolve();
    } catch (err) {
      console.log('ADD REBOUND API ERR: ', err);
      reject(err);
    }
  });
};

export const addAssistApi = async ({ game, selectedTeam, playerId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const player = game[selectedTeam].players[playerId];
      const newStats = {
        ...player.stats,
        assists: player.stats.assists + 1
      };

      const newGame = getNewGameStats({ game, selectedTeam, player, playerId, newStats });
      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      resolve();
    } catch (err) {
      console.log('ADD ASSIST API ERR: ', err);
      reject(err);
    }
  });
};

export const addFoulApi = async ({ game, selectedTeam, playerId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const player = game[selectedTeam].players[playerId];
      const newStats = {
        ...player.stats,
        fouls: player.stats.fouls + 1
      };

      const newGame = getNewGameStats({ game, selectedTeam, player, playerId, newStats });
      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      resolve();
    } catch (err) {
      console.log('ADD FOUL API ERR: ', err);
      reject(err);
    }
  });
};

export const updateLastActionsApi = async (game, lastActions) => {
  return new Promise(async (resolve, reject) => {
    try {
      const newGame = {
        actions: lastActions
      };

      const gameRef = doc(db, 'game', game.id);
      await updateDoc(gameRef, newGame);

      resolve();
    } catch (err) {
      console.log('UPDATE LAST ACTIONS API ERR: ', err);
      reject(err);
    }
  });
};

export const undoLastActionApi = async (lastActions, game) => {
  return new Promise(async (resolve, reject) => {
    try {
      const lastAction = lastActions[lastActions.length - 1];
      if (lastAction) {
        if (lastAction.action === 'addMadeShot' || lastAction.action === 'addAttemptedShot') {
          const { team, playerId, points } = lastAction;
          const player = game[team].players[playerId];
          const type = lastAction.action === 'addAttemptedShot' ? 'attempted' : 'made'

          const newStats = {
            ...player.stats,
            points: {
              ...player.stats.points,
              [type]: player.stats.points[type].map((val, index) => {
                if (index === points) {
                  return val - 1;
                }

                return val;
              })
            }
          };

          let newGame = getNewGameStats({ game, selectedTeam: team, player, playerId, newStats });
          if (type === 'made') {
            const newScore = game[team].score - parseInt(points);
            newGame = getNewGameStats({ game, selectedTeam: team, player, playerId, newStats, newScore });
          }

          const gameRef = doc(db, 'game', game.id);
          await updateDoc(gameRef, newGame);
        } else if (lastAction.action === 'addRebound') {
          const { team, playerId, type } = lastAction;
          const player = game[team].players[playerId];
          const newStats = {
            ...player.stats,
            rebounds: {
              ...player.stats.rebounds,
              [type]: player.stats.rebounds[type] - 1
            }
          };

          const newGame = getNewGameStats({ game, selectedTeam: team, player, playerId, newStats });
          const gameRef = doc(db, 'game', game.id);
          await updateDoc(gameRef, newGame);
        } else if (lastAction.action === 'addAssist') {
          const { team, playerId } = lastAction;
          const player = game[team].players[playerId];
          const newStats = {
            ...player.stats,
            assists: player.stats.assists - 1
          };

          const newGame = getNewGameStats({ game, selectedTeam: team, player, playerId, newStats });
          const gameRef = doc(db, 'game', game.id);
          await updateDoc(gameRef, newGame);
        } else if (lastAction.action === 'addFoul') {
          const { team, playerId } = lastAction;
          const player = game[team].players[playerId];
          const newStats = {
            ...player.stats,
            fouls: player.stats.fouls - 1
          };

          const newGame = getNewGameStats({ game, selectedTeam: team, player, playerId, newStats });
          const gameRef = doc(db, 'game', game.id);
          await updateDoc(gameRef, newGame);
        }
      }

      resolve();
    } catch (err) {
      console.log('UNDO LAST ACTION API ERR: ', err);
      reject(err);
    }
  });
};

export const pushStatsToFirebase = async (game, teamA, teamB) => {
  // Feature flag: keep OFF in production until Q2 player-profile feature is ready.
  // Set REACT_APP_PERSIST_PLAYER_STATS=true in .env to enable.
  if (process.env.REACT_APP_PERSIST_PLAYER_STATS !== 'true') {
    const gameRef = doc(db, 'game', game.id);
    await setDoc(gameRef, { finished: true, endedAt: serverTimestamp() }, { merge: true });
    return;
  }

  const isDrill = game.type === 'drill';
  const createdBy = auth.currentUser?.email || '';
  const batch = writeBatch(db);

  try {
    if (isDrill) {
      const drillStats = game.stats || {};
      for (const [playerId, stats] of Object.entries(drillStats)) {
        // Deterministic ID prevents duplicate docs if game is ended twice
        const playerStatsRef = doc(db, 'player_stats', `${game.id}_${playerId}`);
        batch.set(playerStatsRef, {
          playerId,
          gameId: game.id,
          date: serverTimestamp(),
          type_of_game: 'drill',
          attempts: stats.attempts || 0,
          completions: stats.completions || 0,
          createdBy,
        }, { merge: true });
      }
    } else {
      const teams = [teamA, teamB].filter(Boolean);
      for (const team of teams) {
        const teamStats = game.stats?.[team.id] || {};
        for (const [playerId, stats] of Object.entries(teamStats)) {
          const ft = stats.freeThrows || 0;
          const twos = stats.twos || 0;
          const threes = stats.threes || 0;
          // Deterministic ID prevents duplicate docs if game is ended twice
          const playerStatsRef = doc(db, 'player_stats', `${game.id}_${playerId}`);
          batch.set(playerStatsRef, {
            playerId,
            gameId: game.id,
            teamId: team.id,
            teamName: team.name,
            date: serverTimestamp(),
            type_of_game: game.type || 'pick-up',
            points_scored: ft + twos * 2 + threes * 3,
            shots_made: { ft, twos, threes },
            shots_attempted: {
              ft: ft + (stats.attemptedFreeThrows || 0),
              twos: twos + (stats.attemptedTwos || 0),
              threes: threes + (stats.attemptedThrees || 0),
            },
            rebounds_offensive: stats.offensiveRebounds || 0,
            rebounds_defensive: stats.defensiveRebounds || 0,
            assists: stats.assists || 0,
            fouls: stats.fouls || 0,
            createdBy,
          }, { merge: true });
        }
      }
    }

    const gameRef = doc(db, 'game', game.id);
    batch.set(gameRef, { finished: true, endedAt: serverTimestamp() }, { merge: true });

    await batch.commit();
  } catch (err) {
    console.error('PUSH STATS TO FIREBASE ERR: ', err);
    throw err;
  }
};

export const addOrGetUserApi = async (user) => {
  try {
    const usersRef = collection(db, 'users');
    const userQuery = query(usersRef, where('email', '==', user.email));
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      return { ...userData, isNew: false };
    } else {
      const userDocRef = await addDoc(usersRef, user);
      const newUserSnapshot = await getDoc(userDocRef);
      const userData = newUserSnapshot.data();
      return { ...userData, isNew: true };
    }
  } catch (err) {
    console.log('ADD USER API ERR: ', err);
  }
};

export const getPlayersApi = async ({ user }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const playersRef = collection(db, 'players');
      const playersQuery = query(playersRef, where('createdBy', '==', user.email));
      const playersSnapshot = await getDocs(playersQuery);
      const playersList = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      resolve(playersList);
    } catch (err) {
      console.log('GET PLAYERS API ERR: ', err);
      reject(err);
    }
  });
};

export const addPlayerApi = async ({ player, image }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await runTransaction(db, async (transaction) => {
        try {
          const playersCollection = collection(db, 'players');
          let downloadURL = '';
          const newPlayerRef = await addDoc(playersCollection, {
            ...player,
            avatarUrl: downloadURL // Placeholder
          });

          if (image) {
            // get image blob
            const imageBlob = await fetch(image).then(r => r.blob());
            // Define metadata for the upload
            const metadata = {
              contentType: 'image/png', // Set the MIME type of the image
            };
            // 3. Upload the avatar
            const storageRef = ref(storage, `avatars/players/${newPlayerRef.id}.png`);
            const uploadTaskSnapshot = await uploadBytes(storageRef, imageBlob, metadata);
            downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
            transaction.update(newPlayerRef, { avatarUrl: downloadURL });
          }

          const newPlayer = { ...player, avatarUrl: downloadURL, id: newPlayerRef.id };
          resolve(newPlayer);
        } catch (err) {
          throw err;
        }
      });

    } catch (err) {
      console.log('ADD PLAYER API ERR: ', err);
      reject(err);
    }
  });
};

export const editPlayerApi = async ({ player, image }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await runTransaction(db, async (transaction) => {
        try {
          let downloadURL = null;
          if (image) {
            // get image blob
            const imageBlob = await fetch(image).then(r => r.blob());
            // Define metadata for the upload
            const metadata = {
              contentType: 'image/png', // Set the MIME type of the image
            };
            // 3. Upload the avatar
            const storageRef = ref(storage, `avatars/players/${player.id}.png`);
            const uploadTaskSnapshot = await uploadBytes(storageRef, imageBlob, metadata);
            downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);

            // Check if the image upload was successful
            if (!downloadURL) {
              throw new Error('Image upload failed');
            }
          }

          const docRef = doc(db, 'players', player.id);
          const newPlayer = {
            ...player,
            ...(downloadURL ? { avatarUrl: downloadURL } : {})
          };
          const { id, ...playerForUpdate } = newPlayer;
          transaction.update(docRef, playerForUpdate);

          resolve(newPlayer);
        } catch (err) {
          throw err;
        }
      });

    } catch (err) {
      console.log('EDIT PLAYER API ERR: ', err);
      reject(err);
    }
  });
};

export const deletePlayerApi = async ({ player }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await runTransaction(db, async (transaction) => {
        try {
          const { avatarUrl, id } = player;

          if (avatarUrl) {
            const storageRef = ref(storage, `avatars/players/${id}.png`);
            await deleteObject(storageRef);
          }

          const playerRef = doc(db, 'players', id);
          await deleteDoc(playerRef);

          resolve();
        } catch (err) {
          throw err;
        }
      });

    } catch (err) {
      console.log('DELETE PLAYER API ERR: ', err);
      reject(err);
    }
  });
};

export const addTeamApi = async ({ team, image }) => {
  return new Promise(async (resolve, reject) => {
    let newTeamRef;
    try {
      await runTransaction(db, async (transaction) => {
        try {
          const teamsCollection = collection(db, 'teams');
          const teamsQuery = query(
            teamsCollection,
            and(
              where('createdBy', '==', team.createdBy),
              where('name', '==', team.name)
            )
          );
          const teamSnapshot = await getDocs(teamsQuery);

          if (!teamSnapshot.empty) {
            resolve({ isDuplicate: true });
            return false;
          }

          newTeamRef = await addDoc(teamsCollection, {
            ...team,
            avatarUrl: '' // Placeholder
          });

          let downloadURL = null;
          if (image) {
            // get image blob
            const imageBlob = await fetch(image).then(r => r.blob());
            // Define metadata for the upload
            const metadata = {
              contentType: 'image/png', // Set the MIME type of the image
            };
            // 3. Upload the avatar
            const storageRef = ref(storage, `avatars/teams/${newTeamRef.id}.png`);
            const uploadTaskSnapshot = await uploadBytes(storageRef, imageBlob, metadata);
            downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);

            if (!downloadURL) {
              throw new Error('Image upload failed');
            }

            transaction.update(newTeamRef, { avatarUrl: downloadURL });
          }

          const newTeam = {
            ...team,
            ...(downloadURL ? { avatarUrl: downloadURL } : {}),
            id: newTeamRef.id
          };
          resolve(newTeam);
        } catch (err) {
          // Delete the document if the image upload fails
          transaction.delete(newTeamRef);
          throw err;
        }
      });

    } catch (err) {
      console.log('ADD TEAM API ERR: ', err);
      reject(err);
    }
  });
};

export const editTeamApi = async ({ team, image }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await runTransaction(db, async (transaction) => {
        try {
          let downloadURL = null;
          if (image) {
            // get image blob
            const imageBlob = await fetch(image).then(r => r.blob());
            // Define metadata for the upload
            const metadata = {
              contentType: 'image/png', // Set the MIME type of the image
            };
            // 3. Upload the avatar
            const storageRef = ref(storage, `avatars/teams/${team.id}.png`);
            const uploadTaskSnapshot = await uploadBytes(storageRef, imageBlob, metadata);
            downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);

            // Check if the image upload was successful
            if (!downloadURL) {
              throw new Error('Image upload failed');
            }
          }

          const docRef = doc(db, 'teams', team.id);
          const newTeam = {
            ...team,
            ...(downloadURL ? { avatarUrl: downloadURL } : {})
          };
          const { id, ...teamForUpdate } = newTeam;
          transaction.update(docRef, teamForUpdate);

          resolve(newTeam);
        } catch (err) {
          throw err;
        }
      });

    } catch (err) {
      console.log('EDIT TEAM API ERR: ', err);
      reject(err);
    }
  });
};

export const getTeamsApi = async ({ user }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const teamsRef = collection(db, 'teams');
      const teamsQuery = query(teamsRef, where('createdBy', '==', user.email));
      const teamsSnapshot = await getDocs(teamsQuery);
      const teamsList = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      resolve(teamsList);
    } catch (err) {
      console.log('GET TEAMS API ERR: ', err);
      reject(err);
    }
  });
};

export const deleteTeamApi = async ({ team }) => {
  return new Promise(async (resolve, reject) => {
    try {
      await runTransaction(db, async (transaction) => {
        try {
          const { avatarUrl, id } = team;

          if (avatarUrl) {
            const storageRef = ref(storage, `avatars/teams/${id}.png`);
            await deleteObject(storageRef);
          }

          const teamRef = doc(db, 'teams', id);
          await deleteDoc(teamRef);

          resolve();
        } catch (err) {
          throw err;
        }
      });

    } catch (err) {
      console.log('DELETE TEAM API ERR: ', err);
      reject(err);
    }
  });
};

// Tournament API functions
export const addTournamentApi = async ({ tournament }) => {
  try {
    // Create a copy of the tournament data 
    // with timestamp conversions for dates that are strings
    const tournamentData = {
      ...tournament,
      createdAt: serverTimestamp()
    };
    
    const tournamentRef = collection(db, 'tournaments');
    const newTournamentRef = await addDoc(tournamentRef, tournamentData);
    const newTournamentSnapshot = await getDoc(newTournamentRef);
    const newTournamentData = newTournamentSnapshot.data();
    
    // Handle date conversions safely with type checking
    const resultData = {
      id: newTournamentRef.id,
      ...newTournamentData,
    };
    
    // Only convert timestamps, leave string dates alone
    if (newTournamentData.createdAt && typeof newTournamentData.createdAt.toDate === 'function') {
      resultData.createdAt = newTournamentData.createdAt.toDate().toISOString();
    }
    
    // Leave the original string dates as they are
    resultData.startDate = tournament.startDate;
    resultData.endDate = tournament.endDate;
    
    return resultData;
  } catch (err) {
    console.log('ADD TOURNAMENT API ERR: ', err);
    throw err;
  }
};

export const getTournamentsApi = async ({ user }) => {
  try {
    const tournamentsRef = collection(db, 'tournaments');
    const tournamentsQuery = query(tournamentsRef, where('createdBy', '==', user.email));
    const tournamentsSnapshot = await getDocs(tournamentsQuery);
    
    return tournamentsSnapshot.docs.map(doc => {
      const data = doc.data();
      const result = { 
        id: doc.id, 
        ...data 
      };
      
      // Safely convert Firebase timestamps to ISO strings
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        result.createdAt = data.createdAt.toDate().toISOString();
      }
      
      // For startDate and endDate, keep them as they are (they should already be strings)
      
      return result;
    });
  } catch (err) {
    console.log('GET TOURNAMENTS API ERR: ', err);
    throw err;
  }
};

export const updateTournamentApi = async ({ tournament }) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournament.id);
    const { id, ...tournamentData } = tournament;
    await updateDoc(tournamentRef, tournamentData);
    return tournament;
  } catch (err) {
    console.log('UPDATE TOURNAMENT API ERR: ', err);
    throw err;
  }
};

export const deleteTournamentApi = async ({ tournamentId }) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    await deleteDoc(tournamentRef);
    return tournamentId;
  } catch (err) {
    console.log('DELETE TOURNAMENT API ERR: ', err);
    throw err;
  }
};

export const getTournamentByIdApi = async ({ tournamentId }) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentSnapshot = await getDoc(tournamentRef);
    
    if (!tournamentSnapshot.exists()) {
      throw new Error(`Tournament with ID ${tournamentId} not found`);
    }
    
    const tournamentData = tournamentSnapshot.data();
    const result = {
      id: tournamentSnapshot.id,
      ...tournamentData
    };
    
    // Safely convert Firebase timestamps to ISO strings
    if (tournamentData.createdAt && typeof tournamentData.createdAt.toDate === 'function') {
      result.createdAt = tournamentData.createdAt.toDate().toISOString();
    }
    
    // For startDate and endDate, keep them as they are (they should already be strings)
    
    return result;
  } catch (err) {
    console.log('GET TOURNAMENT BY ID API ERR: ', err);
    throw err;
  }
};

export const addPlayerStatsApi = async (playerId, gameId, date, type_of_game) => {
  try {
    const playerStatsCollection = collection(db, 'player_stats');
    const newPlayerStats = {
      playerId,
      // playerName,
      gameId,
      date,
      points_scored: 0,
      shots_attempted: [0, 0, 0],
      shots_made: [0, 0, 0],
      rebounds_offensive: 0,
      rebounds_defensive: 0,
      assists: 0,
      fouls: 0,
      type_of_game,
    };
    const docRef = await addDoc(playerStatsCollection, newPlayerStats);
    return docRef.id;
  } catch (err) {
    console.error('Error adding player stats:', err);
  }
};

export const updatePlayerStatsApi = async (playerStatsId, updatedFields) => {
  try {
    const playerStatsRef = doc(db, 'player_stats', playerStatsId);
    await updateDoc(playerStatsRef, updatedFields);
  } catch (err) {
    console.error('Error updating player stats:', err);
  }
};

export const endGameApi = async (gameId) => {
  try {
    const gameRef = doc(db, 'game', gameId);
    await updateDoc(gameRef, {
      finished: true,
      endedAt: serverTimestamp()
    });
    return true;
  } catch (err) {
    console.log('END GAME API ERR: ', err);
    return false;
  }
};
